import { put, select } from 'redux-saga/effects';
import { putAndHandleAPIError } from '../../api/API.sagas';
import { getCountry } from '../../iwIntlProvider/CountryProvider';
import { API_GET_PRODUCT_OFFERS } from '../../api/lendingAPI/LendingAPI.actions';
import { REQUEST_CHANGE_ROUTER } from '../../iwRouter/IwRouter.actions';
import * as selector from './accountsSelector';
import { MULTI_LOAN_DASHBOARD_READY } from './Dashboard.actions';
import { getDashboardStatus, INELIGIBLE_STATUS } from '../../api/CBILSSelector';
import { hasOpenApplication } from './multiLoanDashboardSelector';
import { hasCreatedFlexiLoanApplicationInAccountPages } from '../applyFlexiLoanView/applyFlexiLoanSelectors';
import { hasRLSAccount as hasRLSAccountSelector } from '../RLS/rlsSelectors';
import { hasApplicationWithProductType } from '../../api/lendingAPI/ApplicationSelector';
import { RLS_PRODUCT_TYPE } from '../RLS/dashboard/dashboardSelectors';

export function* activateMultiLoanDashboard() {
    yield* fetchProductOffersWhenRequired();

    yield handleRedirects();

    yield put({ type: MULTI_LOAN_DASHBOARD_READY });
}

function* handleRedirects() {
    const hasFlexiLoanApplication = yield select((state) =>
        hasApplicationWithProductType(state, selector.FLEXI_LOAN_PRODUCT_TYPE)
    );
    const disableRedirect = yield select(
        (state) => state?.multiLoanDashboard?.disableRedirect
    );

    const formalOfferProductType = yield select(
        selector.getLatestFormalOfferProductType
    );
    const isFlexiFormalOffer =
        formalOfferProductType === selector.FLEXI_LOAN_PRODUCT_TYPE;

    if (disableRedirect) return;
    if (getCountry() !== 'UK') return;
    if (hasFlexiLoanApplication || isFlexiFormalOffer) return;

    const {
        nonCBILSAccount,
        hasCBILSAccountOrApplication,
        hasRLSApplication,
        hasRLSAccount
    } = yield select((state) => ({
        nonCBILSAccount: Boolean(selector.getAccountByProductType(state)),
        hasCBILSAccountOrApplication:
            getDashboardStatus(state) !== INELIGIBLE_STATUS,
        hasRLSAccount: hasRLSAccountSelector(state),
        hasRLSApplication: hasApplicationWithProductType(
            state,
            RLS_PRODUCT_TYPE
        )
    }));

    const hasOnlyCBILSAccount =
        hasCBILSAccountOrApplication && !nonCBILSAccount;
    const shouldRedirectToRLS = hasRLSApplication || hasRLSAccount;

    if (hasOnlyCBILSAccount) {
        return yield put({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: '/CBILS/' }
        });
    }

    const hasValidNonRecoveryLoanAccounts = yield select((state) =>
        selector.hasOtherValidAccounts(state, RLS_PRODUCT_TYPE)
    );

    if (shouldRedirectToRLS && !hasValidNonRecoveryLoanAccounts) {
        return yield put({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: '/recovery-loan-scheme/' }
        });
    }
}

function* fetchProductOffersWhenRequired() {
    const hasOpenFlexiLoanApplication = yield select((state) =>
        hasOpenApplication(state, selector.FLEXI_LOAN_PRODUCT_TYPE)
    );
    const primaryAccount = yield select(selector.getAccountByProductType);
    const hasApprovalExpired = yield select(selector.hasApprovalExpired);
    const outstandingPrincipal = yield select(selector.getOutstandingPrincipal);
    const latestFormalOffer = yield select(selector.hasLatestFormalOffer);
    const hasCreatedFlexiApplication = yield select(
        hasCreatedFlexiLoanApplicationInAccountPages
    );

    const loanStatus = primaryAccount?.loan_status;

    // only call product offers when PENDING or APPLY_FLEXI_LOAN.
    // Dashboard status is dependant on product offers
    const isApproved = latestFormalOffer && !hasApprovalExpired;
    const isExpired = hasApprovalExpired && !loanStatus;
    const isEnded = loanStatus === selector.ENDED_STATUS;
    const isOutstanding = !isEnded && outstandingPrincipal > 0.1;
    const isActiveRequest = !isApproved && !isExpired && !isOutstanding;
    const loanEndedApplyingForTopup = isEnded && hasCreatedFlexiApplication;

    if (
        ((!isEnded &&
            (hasOpenFlexiLoanApplication || hasCreatedFlexiApplication)) ||
            (hasOpenFlexiLoanApplication && !loanEndedApplyingForTopup)) &&
        isActiveRequest &&
        ![
            selector.OVERDUE_STATUS,
            selector.AWAITING_FUNDS_STATUS,
            selector.TOPUP_STATUS
        ].includes(loanStatus) &&
        getCountry() === 'UK'
    ) {
        yield putAndHandleAPIError({
            type: API_GET_PRODUCT_OFFERS
        });
    }
}
